import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icons } from '../utils/icons';
import { tasksService } from '../services/tasksService';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { diasEspera as calcDias, requiereAtencion } from '../utils/urgencia';
import { getCached, setCached, clearCached, formatAge } from '../utils/apiCache';
import { getMessagingInstance, getToken, FCM_VAPID_KEY } from '../config/firebase';
import { mapEstados, calcularKpisMantenimientos } from '../utils/mantenimientosKpis';

const TECNICOS_IDS = { Benito: 21936, Pablo: 23415, Dani: 21945, Ivan: 31171 };

// Aviso de "nueva venta" (estilo Shopify) al firmar un contrato — push +
// campanita. El permiso del navegador es por origen, no por rol: si ya se
// concedió entrando como técnico en este dispositivo, aquí no se vuelve a
// preguntar, solo se registra el token bajo la identidad de oficina (email)
// para que el backend sepa a quién avisar (netlify/functions/procesarOutbox.js
// → notificarVentaAdmin, disparado por el evento CONTRATO_FIRMADO).
// Id estable por dispositivo/navegador (no por persona) — así el mismo admin
// puede registrar el móvil Y el PC sin que el segundo pise el token del
// primero (saveFcmTokenOficina.js guarda un doc por email+deviceId).
function getDeviceId() {
  const KEY = 'zapata_device_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(KEY, id);
  }
  return id;
}

// Marca en localStorage de que ESTE dispositivo ya se registró — sin esto la
// tarjeta volvía a aparecer en cada visita al Dashboard pidiendo "volver a
// registrar" aunque ya estuviera todo activo.
const VENTA_PUSH_REGISTRADO_KEY = 'zapata_venta_push_registrado';

function VentaAdminNotificationCard({ role }) {
  const getPermission = () => {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  };
  const [status, setStatus] = useState(getPermission);
  const [loading, setLoading] = useState(false);
  // yaRegistrado: se guardó en una visita anterior — se calcula una sola vez
  // al montar. done: se acaba de registrar EN ESTA sesión (para mostrar la
  // confirmación antes de que la tarjeta desaparezca en la próxima visita).
  const [yaRegistrado] = useState(() => {
    try { return localStorage.getItem(VENTA_PUSH_REGISTRADO_KEY) === '1'; } catch { return false; }
  });
  const [done, setDone] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const handleEnable = useCallback(async () => {
    setLoading(true); setErrMsg('');
    try {
      const permission = await Notification.requestPermission();
      setStatus(permission);
      if (permission !== 'granted') { setLoading(false); return; }

      const messaging = await getMessagingInstance();
      if (!messaging) { setErrMsg('Mensajería no disponible en este navegador'); setLoading(false); return; }

      const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      const token = await getToken(messaging, { vapidKey: FCM_VAPID_KEY, serviceWorkerRegistration: swReg });
      if (!token) { setErrMsg('No se pudo obtener token FCM'); setLoading(false); return; }

      const resp = await fetch('/.netlify/functions/saveFcmTokenOficina', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ token, deviceId: getDeviceId() }),
      });
      if (resp.ok) {
        setDone(true);
        try { localStorage.setItem(VENTA_PUSH_REGISTRADO_KEY, '1'); } catch { /* modo privado, etc. */ }
      } else {
        const data = await resp.json().catch(() => ({}));
        setErrMsg(`Error al guardar: ${data.error || resp.status}`);
      }
    } catch (e) {
      setErrMsg(`Error: ${e.message}`);
    }
    setLoading(false);
  }, []);

  // Ya registrado en una visita anterior y el permiso sigue concedido: no hay
  // nada que pedirle al admin, así que la tarjeta no se muestra. Si en algún
  // momento se revoca el permiso (denied/default), vuelve a aparecer.
  if (status === 'granted' && yaRegistrado && !done) return null;

  if (status === 'unsupported') return null;

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
          <span className="text-lg">💰</span>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-800">Avisos push</p>
          <p className="text-xs text-gray-400">{role === 'admin' ? 'Nueva venta y rutas sin cerrar' : 'Rutas sin cerrar'}, en este dispositivo</p>
        </div>
        <div className="ml-auto flex-shrink-0">
          {status === 'granted'
            ? <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-1 rounded-full">Permiso concedido</span>
            : status === 'denied'
            ? <span className="text-xs bg-red-100 text-red-700 font-semibold px-2 py-1 rounded-full">Bloqueadas</span>
            : <span className="text-xs bg-gray-100 text-gray-500 font-semibold px-2 py-1 rounded-full">Sin activar</span>
          }
        </div>
      </div>

      {status === 'granted' && done && (
        <p className="text-xs text-green-600 text-center py-1">✓ Dispositivo registrado — recibirás el aviso aquí</p>
      )}
      {status === 'granted' && !done && (
        <button onClick={handleEnable} disabled={loading}
          className="w-full py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors">
          {loading ? 'Registrando…' : 'Registrar este dispositivo'}
        </button>
      )}
      {status === 'denied' && (
        <p className="text-xs text-red-600 text-center py-1">
          Las notificaciones están bloqueadas para esta web en este navegador — actívalas en los ajustes del navegador/móvil.
        </p>
      )}
      {status === 'default' && (
        <button onClick={handleEnable} disabled={loading}
          className="w-full py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors">
          {loading ? 'Activando…' : 'Activar avisos de venta'}
        </button>
      )}
      {errMsg && <p className="text-xs text-red-600 text-center mt-1 break-all">{errMsg}</p>}
    </div>
  );
}

// Órdenes cuya sincronización con StelOrder quedó en stelSyncEstado='error'
// (6 intentos agotados en syncStelOrderPendientes.js — ver también el aviso
// push a admin en checkStelSyncErrorsAlert.js). Carga aparte de la llamada
// grande de arriba (getOperacionesKPIs) a propósito: es un aviso secundario,
// no forma parte de los KPIs de negocio, y así un fallo aquí nunca bloquea
// el resto del Dashboard. Se oculta sola cuando no hay nada que mostrar.
function StelSyncErroresCard() {
  const [errores, setErrores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [msg, setMsg] = useState('');

  const cargar = useCallback(async () => {
    try {
      const resp = await fetch('/.netlify/functions/getStelSyncErrores', { headers: { ...authService.authHeader() } });
      const data = await resp.json();
      if (data.success) setErrores(data.errores || []);
    } catch { /* aviso secundario — un fallo aquí no debe mostrarse como error del Dashboard */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleReintentar = async () => {
    if (!errores.length || retrying) return;
    setRetrying(true); setMsg('');
    try {
      const resp = await fetch('/.netlify/functions/reintentarSyncStelOrder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ items: errores.map(e => ({ coleccion: e.coleccion, id: e.id })) }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      setMsg(`${data.reintentadas} de ${data.total} en cola de nuevo — tardarán hasta 5 min en sincronizar.`);
      // El cron por lotes (syncStelOrderPendientes.js) corre cada 5 min —
      // recarga sola pasado ese ciclo para reflejar si ya se resolvieron.
      setTimeout(cargar, 6 * 60 * 1000);
    } catch (e) {
      setMsg('Error al reintentar: ' + e.message);
    } finally {
      setRetrying(false);
    }
  };

  if (loading || errores.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-red-200">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <span className="text-lg">⚠️</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-800">Errores de sincronización con StelOrder</p>
            <p className="text-xs text-gray-400">
              {errores.length} elemento{errores.length !== 1 ? 's' : ''} sin poder sincronizarse tras 6 intentos
            </p>
          </div>
        </div>
        <button
          onClick={handleReintentar}
          disabled={retrying}
          className="flex-shrink-0 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors"
        >
          {retrying ? 'Reintentando…' : 'Reintentar todas'}
        </button>
      </div>
      <div className="space-y-1.5 max-h-40 overflow-y-auto">
        {errores.map(e => (
          <div key={`${e.coleccion}-${e.id}`} className="flex items-center justify-between gap-2 bg-red-50 rounded-lg px-3 py-1.5">
            <span className="text-xs font-semibold text-gray-700 truncate">
              <span className="text-gray-400 font-normal">{e.tipo === 'incidencia' ? 'Incidencia' : 'Orden'} · </span>
              {e.referencia}{e.clienteNombre ? ` — ${e.clienteNombre}` : ''}
            </span>
            <span className="text-[10px] text-red-500 flex-shrink-0" title={e.error || ''}>
              {e.intentos ?? '—'} intentos
            </span>
          </div>
        ))}
      </div>
      {msg && <p className="text-xs text-gray-500 mt-2">{msg}</p>}
    </div>
  );
}

// Aviso si procesarBandejaGastos.js (cron horario) no pudo conectar con el
// buzón de gastos de proveedor — mismo criterio que StelSyncErroresCard: se
// oculta sola si todo va bien, un fallo aquí no debe tumbar el Dashboard.
function BuzonGastosErrorCard() {
  const [estado, setEstado] = useState(null);

  useEffect(() => {
    fetch('/.netlify/functions/getEstadoBuzonGastos', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => { if (data.success) setEstado(data.estado); })
      .catch(() => {});
  }, []);

  if (!estado || estado.ok !== false) return null;

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-red-200">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
          <span className="text-lg">⚠️</span>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-800">No se pudo conectar con el buzón de gastos</p>
          <p className="text-xs text-gray-400 truncate" title={estado.ultimoError || ''}>{estado.ultimoError || 'Error desconocido'}</p>
        </div>
      </div>
    </div>
  );
}

// calcDias / requiereAtencion: fuente única en ../utils/urgencia.

const StatCard = ({ icon, label, value, sub, color, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all p-5 ${onClick ? 'cursor-pointer hover:scale-[1.01]' : 'cursor-default'}`}
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}>
      {icon}
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-sm font-medium text-gray-700 mt-0.5">{label}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </button>
);

const AlertRow = ({ referencia, cliente, dias, estado }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-800 truncate">{referencia}</p>
      {cliente && <p className="text-xs text-gray-400 truncate">{cliente}</p>}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
      <span className="text-xs text-gray-500">{estado}</span>
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${dias > 30 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
        {dias}d
      </span>
    </div>
  </div>
);

const FILTROS = [
  { id: 'year',  label: 'Este año' },
  { id: 'month', label: 'Este mes' },
  { id: 'week',  label: 'Esta semana' },
  { id: 'all',   label: 'Histórico' },
];

const dentroDelPeriodo = (dateStr, filtro) => {
  if (filtro === 'all') return true;
  if (!dateStr) return false; // sin fecha → excluir en cualquier filtro de periodo
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  const hoy = new Date();
  if (filtro === 'year') {
    return d.getFullYear() === hoy.getFullYear();
  }
  if (filtro === 'month') {
    return d.getFullYear() === hoy.getFullYear() && d.getMonth() === hoy.getMonth();
  }
  if (filtro === 'week') {
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7));
    lunes.setHours(0, 0, 0, 0);
    const domingo = new Date(lunes);
    domingo.setDate(lunes.getDate() + 6);
    domingo.setHours(23, 59, 59, 999);
    return d >= lunes && d <= domingo;
  }
  return true;
};

// ── Alertas ERP: facturas vencidas, contratos renovar, stock bajo ─────────────
function AlertasERP() {
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [emitiendo, setEmitiendo] = useState(null); // id del item cuya factura se está emitiendo

  useEffect(() => {
    const load = async () => {
      try {
        const { db: firestoreDb }         = await import('../config/firebase');
        const { collection, getDocs, query, where, orderBy, limit } = await import('firebase/firestore');

        const hace30 = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

        const [factSnap, mantSnap, prodSnap, ordenesFactSnap, solMatSnap] = await Promise.all([
          getDocs(query(collection(firestoreDb, 'facturas'), where('estado', '==', 'emitida'), limit(200))),
          getDocs(query(collection(firestoreDb, 'mantenimientos'), where('estado', '==', 'pendiente_renovar'), limit(50))),
          getDocs(query(collection(firestoreDb, 'productos'), limit(500))),
          getDocs(query(collection(firestoreDb, 'ordenes'), where('pendienteFactura', '==', true), limit(50))),
          getDocs(query(collection(firestoreDb, 'solicitudes_material'), where('estado', 'in', ['pendiente', 'pedido']), limit(50))).catch(() => ({ docs: [] })),
        ]);

        const alertas = [];

        // Órdenes cobradas listas para facturar (requieren validación humana antes de emitir)
        ordenesFactSnap.docs.forEach(d => {
          const o = { id: d.id, ...d.data() };
          if (o.facturada) return; // ya facturada, ignorar
          const ref = o.referencia || o['full-reference'] || o.id;
          alertas.push({
            tipo: 'pendiente_factura',
            id: o.id,
            titulo: `Factura pendiente: ${ref}`,
            sub: `${o.clienteNombre || ''} · Cobrada${o['total-amount'] ? ' · ' + Number(o['total-amount']).toLocaleString('es-ES', { minimumFractionDigits: 2 }) + ' €' : ''}`,
            badge: 'Emitir',
            badgeCls: 'bg-green-100 text-green-700',
            icon: '🧾',
            ordenId: o.id,
          });
        });

        // Facturas vencidas > 30 días
        factSnap.docs.forEach(d => {
          const f = { id: d.id, ...d.data() };
          if (f.fecha && f.fecha <= hace30) {
            const dias = Math.floor((Date.now() - new Date(f.fecha)) / 86400000);
            alertas.push({ tipo: 'factura', id: f.id, titulo: f.referencia || f.clienteNombre, sub: `${f.clienteNombre || ''}${f.importeTotal ? ' · ' + Number(f.importeTotal).toLocaleString('es-ES', { minimumFractionDigits: 2 }) + ' €' : ''}`, dias, badge: `${dias}d`, badgeCls: dias > 60 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700', icon: '💰' });
          }
        });

        // Contratos pendientes de renovar
        mantSnap.docs.forEach(d => {
          const c = { id: d.id, ...d.data() };
          alertas.push({ tipo: 'contrato', id: c.id, titulo: c.clienteNombre || c.nombre || c.id, sub: `${c.pack || ''} · ${c.precioAnual ? c.precioAnual + '€/año' : ''}`.trim().replace(/^·\s*/, ''), badge: 'Renovar', badgeCls: 'bg-amber-100 text-amber-700', icon: '📋' });
        });

        // Solicitudes de material pendientes de gestionar
        (solMatSnap.docs || []).forEach(d => {
          const s = { id: d.id, ...d.data() };
          const nItems = Array.isArray(s.items) ? s.items.length : 0;
          alertas.push({
            tipo: 'material',
            id: s.id,
            titulo: `Material: ${s.ordenRef || s.ordenId}`,
            sub: `${s.clienteNombre || ''}${nItems ? ` · ${nItems} artículo${nItems !== 1 ? 's' : ''}` : ''}${s.tecnicoNombre ? ` · ${s.tecnicoNombre}` : ''}`,
            badge: s.estado === 'pedido' ? 'Pedido' : 'Solicitado',
            badgeCls: s.estado === 'pedido' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700',
            icon: '🧰',
          });
        });

        // Productos con stock bajo — Firestore usa la misma estructura de
        // campos que StelOrder (real-stock/minimum-stock/name), ver AlmacenView.js.
        prodSnap.docs.forEach(d => {
          const p = { id: d.id, ...d.data() };
          const stock = Number(p['real-stock'] ?? 0);
          const min   = Number(p['minimum-stock'] ?? 0);
          if (min > 0 && stock <= min) {
            alertas.push({ tipo: 'stock', id: p.id, titulo: p.name || p.id, sub: `Stock: ${stock} · Mínimo: ${min}`, badge: 'Stock bajo', badgeCls: 'bg-purple-100 text-purple-700', icon: '📦' });
          }
        });

        setItems(alertas.slice(0, 40));
      } catch (e) {
        console.warn('[AlertasERP]', e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;

  const porTipo = {
    pendiente_factura: items.filter(i => i.tipo === 'pendiente_factura').length,
    factura:  items.filter(i => i.tipo === 'factura').length,
    contrato: items.filter(i => i.tipo === 'contrato').length,
    material: items.filter(i => i.tipo === 'material').length,
    stock:    items.filter(i => i.tipo === 'stock').length,
  };

  const emitirFactura = async (item, setItems) => {
    if (emitiendo) return; // ya hay una emisión en curso
    if (!window.confirm(`¿Emitir factura para la orden ${item.titulo.replace('Factura pendiente: ', '')}?\n\nEsta acción creará la factura en StelOrder. Verifica que la orden tenga todos los datos correctos antes de continuar.`)) return;
    setEmitiendo(item.id);
    try {
      const resp = await fetch('/.netlify/functions/crearFacturaSAT', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ ordenId: item.ordenId }),
      });
      const d = await resp.json();
      if (d.success) {
        alert(`Factura creada: ${d.facturaRef}`);
        setItems(prev => prev.filter(i => i.id !== item.id));
      } else {
        alert('Error al crear factura: ' + (d.error || 'Error desconocido'));
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setEmitiendo(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <div className="bg-rose-50 border-b border-rose-100 px-5 py-3 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
        <h3 className="font-bold text-sm text-rose-700">Alertas de gestión</h3>
        <div className="ml-auto flex items-center gap-2">
          {porTipo.pendiente_factura > 0 && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">{porTipo.pendiente_factura} por facturar</span>}
          {porTipo.factura  > 0 && <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">{porTipo.factura} factura{porTipo.factura !== 1 ? 's' : ''}</span>}
          {porTipo.contrato > 0 && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">{porTipo.contrato} contrato{porTipo.contrato !== 1 ? 's' : ''}</span>}
          {porTipo.material > 0 && <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">{porTipo.material} material</span>}
          {porTipo.stock    > 0 && <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">{porTipo.stock} producto{porTipo.stock !== 1 ? 's' : ''}</span>}
        </div>
      </div>
      <div className="px-5 py-2 max-h-64 overflow-y-auto divide-y divide-gray-50">
        {items.map(item => (
          <div key={`${item.tipo}-${item.id}`} className="flex items-center gap-3 py-2.5">
            <span className="text-base flex-shrink-0">{item.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{item.titulo}</p>
              {item.sub && <p className="text-xs text-gray-400 truncate">{item.sub}</p>}
            </div>
            {item.tipo === 'pendiente_factura' ? (
              <button
                onClick={() => emitirFactura(item, setItems)}
                disabled={emitiendo !== null}
                className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {emitiendo === item.id ? 'Emitiendo…' : 'Emitir factura'}
              </button>
            ) : (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${item.badgeCls}`}>
                {item.badge}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Panel Dirección: facturación, cobros y recurrente (solo dirección) ────────
function PanelDireccion({ onNavigate }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { db: firestoreDb } = await import('../config/firebase');
        const { collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');

        const anio = new Date().getFullYear();
        const enAnio = (s) => typeof s === 'string' && s.slice(0, 4) === String(anio);

        const [factSnap, mantResp] = await Promise.all([
          getDocs(query(collection(firestoreDb, 'facturas'), orderBy('fecha', 'desc'), limit(2000)))
            .catch(() => getDocs(query(collection(firestoreDb, 'facturas'), limit(2000)))),
          fetch('/.netlify/functions/getMantenimientos', { headers: { ...authService.authHeader() } })
            .then(r => r.json())
            .catch(() => ({ success: false })),
        ]);

        const facturas = factSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const mants = mantResp && mantResp.success ? (mantResp.mantenimientos || []) : [];

        // Facturado neto del año en curso (mismo criterio que InformesView · ResumenTab)
        const facts  = facturas.filter(f => f.tipo !== 'abono' && enAnio(f.fecha));
        const abonos = facturas.filter(f => f.tipo === 'abono' && enAnio(f.fecha));
        const facturadoNeto =
          facts.reduce((s, f) => s + (Number(f.importeTotal) || 0), 0) -
          abonos.reduce((s, a) => s + (Number(a.importe) || 0), 0);

        const cobrado = facturas
          .filter(f => f.estado === 'cobrada' && enAnio(f.cobro?.fecha || f.fecha))
          .reduce((s, f) => s + (Number(f.cobro?.importe ?? f.importeTotal) || 0), 0);

        const pendienteCobro = facturas
          .filter(f => f.estado === 'emitida')
          .reduce((s, f) => s + (Number(f.importeTotal) || 0), 0);

        // Recurrente de contratos de mantenimiento (mismo criterio que MantenimientosARRTab)
        const ESTADOS_ARR = new Set(['firmado_activo', 'sepa_generado', 'pendiente_factura', 'pendiente_agendar', 'mantenimiento_agendado', 'vence_pronto']);
        const arr = mants
          .filter(m => ESTADOS_ARR.has(m.estado))
          .reduce((s, m) => s + Number(m.precioAnual || 0), 0);
        const arrEnRiesgo = mants
          .filter(m => ['pendiente_renovar', 'vencido', 'sepa_devuelto'].includes(m.estado))
          .reduce((s, m) => s + Number(m.precioAnual || 0), 0);

        setData({ facturadoNeto, cobrado, pendienteCobro, mrr: arr / 12, arr, arrEnRiesgo });
      } catch (e) {
        console.warn('[PanelDireccion]', e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading || !data) return null;

  const anio = new Date().getFullYear();
  const fmt = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

  const kpis = [
    { label: `Facturado ${anio}`,  value: fmt(data.facturadoNeto),  sub: 'neto de abonos' },
    { label: `Cobrado ${anio}`,    value: fmt(data.cobrado),        sub: 'facturas liquidadas' },
    { label: 'Pendiente de cobro', value: fmt(data.pendienteCobro), sub: 'facturas emitidas' },
    { label: 'MRR contratos',      value: fmt(data.mrr),            sub: `ARR ${fmt(data.arr)}` },
    { label: 'ARR en riesgo',      value: fmt(data.arrEnRiesgo),    sub: 'renovar / vencido / devuelto' },
  ];

  return (
    <button
      onClick={() => onNavigate('v2-informes')}
      className="w-full text-left bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all overflow-hidden"
    >
      <div className="bg-slate-50 border-b px-5 py-3 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-slate-400" />
        <h3 className="font-bold text-sm text-slate-700">Dirección — facturación y recurrente</h3>
        <span className="ml-auto text-xs text-slate-400 font-medium">Ver informes →</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 divide-x divide-y lg:divide-y-0 divide-gray-100">
        {kpis.map(k => (
          <div key={k.label} className="p-4">
            <p className="text-xs font-medium text-gray-500">{k.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{k.value}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{k.sub}</p>
          </div>
        ))}
      </div>
    </button>
  );
}

function NotificationsPanel() {
  const [title, setTitle]     = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState(null); // { sent, failed } | 'error'
  const timerRef = useRef(null);

  const clearResult = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setResult(null), 4000);
  };

  const sendReminder = async () => {
    setSending(true);
    setResult(null);
    try {
      const resp = await fetch('/.netlify/functions/sendRouteReminder', { method: 'POST' });
      const data = await resp.json();
      setResult(data);
    } catch { setResult('error'); }
    setSending(false);
    clearResult();
  };

  const sendCustom = async () => {
    if (!title.trim() || !message.trim()) return;
    setSending(true);
    setResult(null);
    try {
      // Primero obtener todos los tokens de Firestore
      const { db } = await import('../config/firebase');
      const { collection, getDocs } = await import('firebase/firestore');
      const snap = await getDocs(collection(db, 'fcm_tokens'));
      const tokens = snap.docs.map(d => d.data().token).filter(Boolean);

      if (!tokens.length) { setResult({ sent: 0, failed: 0 }); setSending(false); clearResult(); return; }

      const resp = await fetch('/.netlify/functions/sendNotification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ tokens, title: title.trim(), message: message.trim() }),
      });
      const data = await resp.json();
      setResult(data);
      if (data.success) { setTitle(''); setMessage(''); }
    } catch { setResult('error'); }
    setSending(false);
    clearResult();
  };

  return (
    <div className="bg-white rounded-2xl border shadow-sm p-5 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        <div>
          <h3 className="font-bold text-gray-800 text-sm">Notificaciones a técnicos</h3>
          <p className="text-xs text-gray-400">Envía avisos push a todos los técnicos</p>
        </div>
      </div>

      {/* Recordatorio rápido */}
      <div className="flex items-center justify-between gap-3 bg-indigo-50 rounded-xl px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-indigo-900">Recordatorio de cierre de ruta</p>
          <p className="text-xs text-indigo-500 mt-0.5">Se envía automáticamente L–V a las 17h. También puedes enviarlo ahora.</p>
        </div>
        <button
          onClick={sendReminder}
          disabled={sending}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors"
        >
          {sending ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '📤'}
          Enviar ahora
        </button>
      </div>

      {/* Notificación personalizada */}
      <div className="space-y-2">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Mensaje personalizado</p>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Título"
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200"
        />
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Mensaje para los técnicos..."
          rows={2}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200"
        />
        <button
          onClick={sendCustom}
          disabled={sending || !title.trim() || !message.trim()}
          className="w-full py-2 bg-gray-800 hover:bg-gray-900 disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          {sending && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          Enviar a todos los técnicos
        </button>
      </div>

      {/* Resultado */}
      {result && result !== 'error' && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl px-3 py-2 text-sm">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {result.sent} enviado{result.sent !== 1 ? 's' : ''}
          {result.failed > 0 && ` · ${result.failed} fallido${result.failed !== 1 ? 's' : ''}`}
        </div>
      )}
      {result === 'error' && (
        <p className="text-xs text-red-500 bg-red-50 rounded-xl px-3 py-2">Error al enviar. Comprueba las credenciales de Firebase en las variables de entorno.</p>
      )}
    </div>
  );
}

// ── Fichaje card para personal de oficina ────────────────────────────────────
const getSpainToday = () =>
  new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid' }).format(new Date());

function FichajeCardOficina({ user }) {
  const [fichaje,  setFichaje]  = useState(undefined);
  const [loading,  setLoading]  = useState(true);
  const [acting,   setActing]   = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const today  = getSpainToday();
  const userId = user?.role === 'tecnico' && user?.employeeId
    ? String(user.employeeId)
    : String(user?.id || '');

  const loadFichaje = useCallback(async () => {
    if (!userId) return;
    try {
      const resp = await fetch(`/.netlify/functions/getFichajes?fecha=${today}`, { headers: { ...authService.authHeader() } });
      const data = await resp.json();
      if (data.success) {
        const mine = (data.fichajes || []).find(f => String(f.tecnicoId) === userId);
        setFichaje(mine || null);
      }
    } catch (e) { console.error('FichajeCard error:', e); }
    finally { setLoading(false); }
  }, [userId, today]);

  useEffect(() => { loadFichaje(); }, [loadFichaje]);

  const handleFichar = async (tipo) => {
    setActing(true); setErrorMsg('');
    try {
      const resp = await fetch('/.netlify/functions/createFichaje', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          tipo,
          tecnicoId:    userId,
          tecnicoNombre: user?.name || user?.email || '',
          fecha:         today,
          creadoPor:    'oficina',
        }),
      });
      const data = await resp.json();
      if (data.success) setFichaje(data.fichaje);
      else { setErrorMsg(data.error || 'Error al fichar'); if (resp.status === 409) loadFichaje(); }
    } catch { setErrorMsg('Error de conexión'); }
    finally { setActing(false); }
  };

  if (loading) return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border flex items-center gap-3">
      <div className="w-5 h-5 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin flex-shrink-0" />
      <p className="text-sm text-gray-400">Cargando fichaje...</p>
    </div>
  );

  const isCerrado = fichaje?.estado === 'cerrado' || fichaje?.estado === 'corregido';
  const isActivo  = fichaje?.estado === 'activo';

  return (
    <div className={`rounded-2xl p-4 shadow-sm border ${
      isCerrado ? 'bg-green-50 border-green-200'
      : isActivo ? 'bg-amber-50 border-amber-200'
      : 'bg-white border-gray-100'
    }`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isCerrado ? 'bg-green-100' : isActivo ? 'bg-amber-100' : 'bg-red-50'
        }`}>
          <svg className={`w-5 h-5 ${isCerrado ? 'text-green-600' : isActivo ? 'text-amber-600' : 'text-red-600'}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-sm">Fichaje del día</p>
          {isCerrado && (
            <p className="text-xs text-green-700 font-semibold mt-0.5">
              {fichaje.entradaHora} → {fichaje.salidaHora}
              {fichaje.duracionMinutos != null && ` · ${Math.floor(fichaje.duracionMinutos / 60)}h ${fichaje.duracionMinutos % 60}m efectivos`}
            </p>
          )}
          {isActivo && (
            <p className="text-xs text-amber-700 font-semibold mt-0.5">En jornada desde {fichaje.entradaHora}</p>
          )}
          {!fichaje && (
            <p className="text-xs text-gray-400 mt-0.5">Sin registro hoy · RDL 8/2019</p>
          )}
        </div>
        {errorMsg && <p className="text-xs text-red-600 flex-shrink-0 max-w-[140px]">{errorMsg}</p>}
        {isCerrado ? (
          <span className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-green-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Jornada cerrada
          </span>
        ) : (
          <button
            onClick={() => handleFichar(isActivo ? 'salida' : 'entrada')}
            disabled={acting}
            className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white rounded-xl transition-colors disabled:opacity-50 ${
              isActivo ? 'bg-amber-500 hover:bg-amber-600' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {acting && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {isActivo ? 'Fichar salida' : 'Fichar entrada'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Panel de operaciones del día ─────────────────────────────────────────────
// Tarjetas: Órdenes hoy / % completado / SLA en alerta / Rendimiento / Ruta
// cerrada hoy — todo viene de getWorkordersDay.js y getOperacionesKPIs.js
// (mismo criterio de "terminada"/"en alerta" que Analítica de Órdenes de
// trabajo, ver ESTADOS_CIERRE en ambos backends), nada se recalcula aquí.
function PanelDiario({ cargaHoy, onNavigate, rutasHoy, slaAlerta, completadasHoy, rendimientoHoy }) {
  const totalHoy = Object.values(cargaHoy).reduce((s, n) => s + (Number(n) || 0), 0);
  const fechaLabel = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  const fmtEur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl shadow-lg p-5 text-white">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-red-200 text-xs font-semibold uppercase tracking-wider">Operaciones del día</p>
          <h3 className="text-lg font-bold capitalize">{fechaLabel}</h3>
        </div>
        <div className="flex items-center gap-3">
          {slaAlerta?.count > 0 && (
            <button onClick={() => onNavigate?.('v2-ordenes', { tab: 'analitica' })}
              className="flex items-center gap-1.5 bg-red-800/60 hover:bg-red-800/80 px-3 py-1.5 rounded-xl text-sm font-bold transition-colors">
              ⚠️ {slaAlerta.count} en alerta SLA
            </button>
          )}
          <button onClick={() => onNavigate?.('v2-ordenes', { tab: 'analitica' })}
            className="bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-xl text-sm font-bold transition-colors">
            Ver rutas →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-white/15 rounded-xl p-3">
          <p className="text-red-200 text-xs font-semibold mb-1">Órdenes hoy</p>
          <p className="text-2xl font-bold">{totalHoy}</p>
        </div>
        <div className="bg-white/15 rounded-xl p-3">
          <p className="text-red-200 text-xs font-semibold mb-1">% completado</p>
          <p className="text-2xl font-bold">{completadasHoy ? `${completadasHoy.pct}%` : '—'}</p>
          {completadasHoy && (
            <p className="text-[11px] text-red-200/80 mt-0.5">{completadasHoy.count} de {totalHoy}</p>
          )}
        </div>
        <div className="bg-white/15 rounded-xl p-3">
          <p className="text-red-200 text-xs font-semibold mb-1">SLA en alerta</p>
          <p className={`text-2xl font-bold ${slaAlerta?.count > 0 ? 'text-yellow-300' : ''}`}>
            {slaAlerta ? `${slaAlerta.pct}%` : '—'}
          </p>
          {slaAlerta && (
            <p className="text-[11px] text-red-200/80 mt-0.5">{slaAlerta.count} de {slaAlerta.base} pendientes</p>
          )}
        </div>
        <div className="bg-white/15 rounded-xl p-3">
          <p className="text-red-200 text-xs font-semibold mb-1">Rendimiento</p>
          <p className="text-2xl font-bold">{rendimientoHoy !== null ? fmtEur(rendimientoHoy) : '—'}</p>
          <p className="text-[11px] text-red-200/80 mt-0.5">facturado en órdenes cerradas hoy</p>
        </div>
        {rutasHoy && (
          <button onClick={() => onNavigate?.('v2-ordenes', { tab: 'rutas' })}
            className="bg-white/15 hover:bg-white/25 rounded-xl p-3 text-left transition-colors">
            <p className="text-red-200 text-xs font-semibold mb-1">Ruta cerrada hoy</p>
            <p className="text-2xl font-bold">{rutasHoy.completitudHoy ?? 0}%</p>
            {/* sinCerrarHoyConRuta (no tecnicosSinCerrarHoy) — solo cuenta a
                quien de verdad tenía ruta esperada hoy (orden asignada y sin
                ausencia), ver _routeExpectations.js */}
            {rutasHoy.sinCerrarHoyConRuta?.length > 0 && (
              <p className="text-[11px] text-yellow-200 mt-0.5 truncate" title={rutasHoy.sinCerrarHoyConRuta.map(t => t.nombre).join(', ')}>
                Sin cerrar: {rutasHoy.sinCerrarHoyConRuta.map(t => t.nombre).join(', ')}
              </p>
            )}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.entries(cargaHoy).map(([nombre, count]) => (
          <div key={nombre} className="flex items-center gap-1.5 bg-white/15 px-3 py-1.5 rounded-xl">
            <span className="w-2 h-2 rounded-full bg-white/70" />
            <span className="text-sm font-medium">{nombre}</span>
            <span className={`text-sm font-bold ${Number(count) === 0 ? 'text-red-300' : ''}`}>{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Panel de contratos (solo admin) ──────────────────────────────────────────
// Mismo diseño que PanelDiario, en gris claro en vez de rojo — resumen de
// firmas del día/mes y ARR, justo debajo de "Operaciones del día". Datos de
// calcularKpisMantenimientos (ver DashboardView.cargar → contratosResumen).
function PanelContratos({ resumen, onNavigate }) {
  const fmtEur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="bg-gray-100 rounded-2xl shadow-lg p-5 text-gray-900">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Contratos</p>
          <h3 className="text-lg font-bold">Mantenimiento</h3>
        </div>
        <button onClick={() => onNavigate?.('mantenimientos')}
          className="bg-white hover:bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-xl text-sm font-bold text-gray-700 transition-colors">
          Ver contratos →
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold mb-1">Firmados hoy</p>
          <p className="text-2xl font-bold">{resumen.firmadosHoy}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold mb-1">Pendiente de firmar</p>
          <p className={`text-2xl font-bold ${resumen.pendienteFirmar > 0 ? 'text-amber-600' : ''}`}>{resumen.pendienteFirmar}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold mb-1">Firmados este mes</p>
          <p className="text-2xl font-bold">{resumen.firmadosEsteMes}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold mb-1">MRR</p>
          <p className="text-2xl font-bold">{fmtEur(resumen.mrr)}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold mb-1">ARR</p>
          <p className="text-2xl font-bold">{fmtEur(resumen.arr)}</p>
        </div>
      </div>
    </div>
  );
}

// ── Diagnóstico del motor de rutas (herramienta PERMANENTE de medición) ──────
// Puntúa cada ruta guardada en rutas_planificadas contra las reglas de
// negocio del motor (ver netlify/functions/_diagnosticoReglas.js). De las 11
// reglas acordadas con dirección (2026-10-05) solo 4 son medibles con lo que
// se guarda hoy (ventana/comida, capacidad, geografía, menor recorrido); las
// otras 7 necesitan el rediseño del motor (fases 6b-6f, todavía bloqueado) y
// se muestran aquí como "sin datos todavía", nunca como incumplidas.
const REGLA_LABEL = {
  1: 'Ventana 9:45-17:30 y comida',
  2: 'Máx. 5 órdenes/día',
  3: 'Plazos obligatorios',
  4: 'Prioridad completa',
  5: 'Habilidades por técnico',
  6: 'Instalaciones grandes',
  7: 'Máx. 2 zonas por ruta',
  8: 'Menor recorrido',
  9: 'Material ya recibido',
  10: 'Continuidad de técnico',
  11: 'Estabilidad día+franja',
};

function DiagnosticoRutasCard() {
  const [expandido, setExpandido] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const ejecutar = async () => {
    setExpandido(true);
    if (data) return; // ya cargado esta visita — "Actualizar" lo repite explícitamente
    setLoading(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/diagnosticoRutas?dias=21', { headers: { ...authService.authHeader() } });
      const d = await resp.json();
      if (!resp.ok || !d.success) throw new Error(d.error || 'Error al calcular el diagnóstico');
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const recargar = async () => {
    setLoading(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/diagnosticoRutas?dias=21', { headers: { ...authService.authHeader() } });
      const d = await resp.json();
      if (!resp.ok || !d.success) throw new Error(d.error || 'Error al calcular el diagnóstico');
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const reglasMedibles  = [1, 2, 7, 8];
  const reglasSinDatos  = [3, 4, 5, 6, 9, 10, 11];

  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <div className="bg-indigo-50 border-b border-indigo-100 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-indigo-400" />
          <h3 className="font-bold text-sm text-indigo-700">Motor de rutas — diagnóstico de reglas</h3>
        </div>
        {!expandido ? (
          <button onClick={ejecutar} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors">
            Ejecutar diagnóstico
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={recargar} disabled={loading} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors">
              {loading ? 'Calculando…' : 'Actualizar'}
            </button>
            <button onClick={() => setExpandido(false)} className="px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-semibold transition-colors">
              Ocultar
            </button>
          </div>
        )}
      </div>

      {expandido && (
        <div className="px-5 py-4">
          {loading && !data && <p className="text-sm text-gray-400 text-center py-4">Calculando…</p>}
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

          {data && (
            <div className="space-y-4">
              <p className="text-xs text-gray-400">
                {data.totalRutas} ruta{data.totalRutas !== 1 ? 's' : ''} confirmada{data.totalRutas !== 1 ? 's' : ''} entre {data.desde} y {data.hasta}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {reglasMedibles.map(n => {
                  const r = data.porRegla[String(n)];
                  const pct = r?.pctCumplimiento;
                  const color = pct == null ? 'text-gray-400' : pct >= 90 ? 'text-green-600' : pct >= 60 ? 'text-amber-600' : 'text-red-600';
                  return (
                    <div key={n} className="bg-gray-50 rounded-xl p-3">
                      <p className="text-[11px] font-semibold text-gray-500 mb-1">Regla {n} · {REGLA_LABEL[n]}</p>
                      <p className={`text-xl font-bold ${color}`}>{pct == null ? '—' : `${pct}%`}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{r?.medibles ?? 0} rutas medibles</p>
                    </div>
                  );
                })}
              </div>

              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                  Pendientes del rediseño del motor (sin datos todavía)
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {reglasSinDatos.map(n => (
                    <span key={n} className="text-[10px] bg-gray-100 text-gray-500 px-2 py-1 rounded-full" title={REGLA_LABEL[n]}>
                      Regla {n}
                    </span>
                  ))}
                </div>
              </div>

              {data.peores.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Rutas con más incumplimientos
                  </p>
                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {data.peores.map((r, i) => {
                      const fallos = reglasMedibles
                        .filter(n => r.checks[n].estado === 'no_cumple')
                        .map(n => `Regla ${n}: ${r.checks[n].detalle}`);
                      return (
                        <div key={`${r.fecha}-${r.tecnico}-${i}`} className="bg-red-50 rounded-lg px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-gray-800">{r.fecha} · {r.tecnico}</span>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-200 text-red-800">
                              {r.incumplidas} incumplimiento{r.incumplidas !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <ul className="mt-1 space-y-0.5">
                            {fallos.map((f, idx) => <li key={idx} className="text-[11px] text-gray-500">{f}</li>)}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {data.totalRutas > 0 && data.peores.length === 0 && (
                <p className="text-sm text-green-600 text-center py-2">🎉 Ninguna ruta incumple las reglas medibles en este periodo</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Rendimiento de Joaquín (subcontrata, garantías Ariston, zona Lorca) ──────
// Excluido del motor de rutas (ver _technicians.js), pero sus plazos ante
// Ariston siguen siendo responsabilidad nuestra — decisión de dirección
// 2026-10-05: medirlo aparte, con SLA 48-72h desde la entrada del aviso.
function RendimientoJoaquinCard() {
  const [expandido, setExpandido] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const cargar = async () => {
    setLoading(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/getRendimientoJoaquin?dias=90', { headers: { ...authService.authHeader() } });
      const d = await resp.json();
      if (!resp.ok || !d.success) throw new Error(d.error || 'Error al calcular el informe');
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const ejecutar = () => { setExpandido(true); if (!data) cargar(); };

  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <div className="bg-teal-50 border-b border-teal-100 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-teal-400" />
          <h3 className="font-bold text-sm text-teal-700">Joaquín (subcontrata) — garantías Ariston</h3>
        </div>
        {!expandido ? (
          <button onClick={ejecutar} className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold transition-colors">
            Ver informe
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={cargar} disabled={loading} className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors">
              {loading ? 'Calculando…' : 'Actualizar'}
            </button>
            <button onClick={() => setExpandido(false)} className="px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-semibold transition-colors">
              Ocultar
            </button>
          </div>
        )}
      </div>

      {expandido && (
        <div className="px-5 py-4">
          {loading && !data && <p className="text-sm text-gray-400 text-center py-4">Calculando…</p>}
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

          {data && (
            <div className="space-y-4">
              <p className="text-xs text-gray-400">
                {data.totalOrdenes} orden{data.totalOrdenes !== 1 ? 'es' : ''} entre {data.desde} y {data.hasta} · SLA objetivo: 48-72h desde la entrada del aviso
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] font-semibold text-gray-500 mb-1">Cerradas</p>
                  <p className="text-xl font-bold text-gray-800">{data.cerradas}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] font-semibold text-gray-500 mb-1">Pendientes</p>
                  <p className="text-xl font-bold text-gray-800">{data.pendientes}</p>
                  {(data.pendientesEnAviso > 0 || data.pendientesIncumplidas > 0) && (
                    <p className="text-[10px] text-amber-600 mt-0.5">{data.pendientesEnAviso} en aviso · {data.pendientesIncumplidas} incumplidas</p>
                  )}
                </div>
                <div className="bg-gray-50 rounded-xl p-3 col-span-2 md:col-span-1">
                  <p className="text-[11px] font-semibold text-gray-500 mb-1">SLA cumplido (cerradas)</p>
                  <p className={`text-xl font-bold ${data.sla.pctCumplimiento == null ? 'text-gray-400' : data.sla.pctCumplimiento >= 90 ? 'text-green-600' : data.sla.pctCumplimiento >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                    {data.sla.pctCumplimiento == null ? '—' : `${data.sla.pctCumplimiento}%`}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{data.sla.muestra} con dato</p>
                </div>
              </div>

              {data.detalle.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Por tiempo transcurrido (mayor primero)
                  </p>
                  <div className="space-y-1 max-h-72 overflow-y-auto">
                    {data.detalle.slice(0, 20).map(d => (
                      <div key={d.id} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 ${
                        d.slaEstado === 'incumplida' ? 'bg-red-50' : d.slaEstado === 'aviso' ? 'bg-amber-50' : 'bg-gray-50'
                      }`}>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate">{d.reference} — {d.customerName}</p>
                          <p className="text-[10px] text-gray-400">{d.cerrada ? 'Cerrada' : 'Pendiente'}</p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          d.slaEstado === 'incumplida' ? 'bg-red-200 text-red-800' :
                          d.slaEstado === 'aviso'      ? 'bg-amber-200 text-amber-800' :
                          d.slaEstado === 'ok'         ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {d.horasTranscurridas != null ? `${Math.round(d.horasTranscurridas)}h` : 'sin dato'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.totalOrdenes === 0 && (
                <p className="text-sm text-gray-400 text-center py-2">Sin órdenes de Joaquín en este periodo</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Comparativa del piloto de notas IA, por técnico (Parte 1, punto 5) ──────
// Extiende dos cálculos que ya existían (primera visita resuelta de
// finalizarVisita.js, completitud/tiempo de cierre del propio generador de
// rutas) para poder comparar antes/después de empezar a usar notas IA con
// un técnico. La métrica de primera visita es nueva (se empezó a grabar hoy
// mismo), así que el bucket "antes" de un piloto que arranca ahora saldrá
// vacío — es lo esperado, no un fallo.
function KpiPilotoCard() {
  const [tecnico, setTecnico] = useState('');
  const [inicioPiloto, setInicioPiloto] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const tecnicosDisponibles = Object.keys(TECNICOS_IDS);

  const consultar = async () => {
    if (!tecnico) return;
    setLoading(true); setError(''); setData(null);
    try {
      const resp = await fetch(`/.netlify/functions/getKpiPilotoTecnico?tecnico=${tecnico}&inicioPiloto=${inicioPiloto}`, { headers: { ...authService.authHeader() } });
      const d = await resp.json();
      if (!resp.ok || !d.success) throw new Error(d.error || 'Error al calcular la comparativa');
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const Bloque = ({ titulo, bucket, sufijo }) => (
    <div className="bg-gray-50 rounded-xl p-3">
      <p className="text-[11px] font-semibold text-gray-500 mb-1">{titulo}</p>
      <p className="text-lg font-bold text-gray-800">{bucket.pctResueltas ?? bucket.media ?? '—'}{sufijo}</p>
      <p className="text-[10px] text-gray-400 mt-0.5">{bucket.total} muestra{bucket.total !== 1 ? 's' : ''}</p>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border shadow-sm p-5 space-y-3">
      <div>
        <h3 className="font-bold text-gray-800 text-sm">Comparativa del piloto — por técnico</h3>
        <p className="text-xs text-gray-400">Primera visita resuelta y tiempo de cierre, antes/después de empezar con notas IA</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {tecnicosDisponibles.map(t => (
          <button key={t} onClick={() => setTecnico(t)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              tecnico === t ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}>
            {t}
          </button>
        ))}
        <input type="date" value={inicioPiloto} onChange={e => setInicioPiloto(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
        <button onClick={consultar} disabled={!tecnico || loading}
          className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white rounded-lg text-xs font-semibold transition-colors">
          {loading ? 'Calculando…' : 'Consultar'}
        </button>
      </div>

      {error && <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

      {data && (
        <div className="space-y-2">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Primera visita resuelta</p>
          <div className="grid grid-cols-2 gap-3">
            <Bloque titulo={`Antes de ${data.inicioPiloto}`} bucket={data.primeraVisita.antes} sufijo="%" />
            <Bloque titulo={`Desde ${data.inicioPiloto}`}    bucket={data.primeraVisita.despues} sufijo="%" />
          </div>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider pt-1">Tiempo medio de cierre</p>
          <div className="grid grid-cols-2 gap-3">
            <Bloque titulo={`Antes de ${data.inicioPiloto}`} bucket={data.completitud.antes} sufijo="d" />
            <Bloque titulo={`Desde ${data.inicioPiloto}`}    bucket={data.completitud.despues} sufijo="d" />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Oportunidades comerciales por técnico (decisión 2026-10-06, última
// pieza del plan de notas IA): cuántas oportunidades reales se le ofrecieron
// (equipo apto + sin contrato, detectado por generarNotaRutaIA.js) y qué %
// acabó convertido en un contrato CAPTADO por ese mismo técnico.
function OportunidadesTecnicoCard() {
  const [tecnico, setTecnico] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const consultar = async (t) => {
    setTecnico(t);
    setLoading(true); setError(''); setData(null);
    try {
      const resp = await fetch(`/.netlify/functions/getOportunidadesTecnico?tecnico=${t}&dias=90`, { headers: { ...authService.authHeader() } });
      const d = await resp.json();
      if (!resp.ok || !d.success) throw new Error(d.error || 'Error al calcular las oportunidades');
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border shadow-sm p-5 space-y-3">
      <div>
        <h3 className="font-bold text-gray-800 text-sm">Oportunidades comerciales — por técnico</h3>
        <p className="text-xs text-gray-400">Equipos aptos (aerotermia/bomba de calor/caldera) sin contrato, detectados en las notas IA · últimos 90 días</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.keys(TECNICOS_IDS).map(t => (
          <button key={t} onClick={() => consultar(t)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              tecnico === t ? 'bg-violet-600 text-white border-violet-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}>
            {t}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-gray-400 text-center py-2">Calculando…</p>}
      {error && <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

      {data && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-gray-800">{data.totalOfrecidas}</p>
              <p className="text-[10px] text-gray-500">Ofrecidas</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-green-600">{data.totalConvertidas}</p>
              <p className="text-[10px] text-gray-500">Convertidas</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className={`text-lg font-bold ${data.pctExito == null ? 'text-gray-400' : data.pctExito >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
                {data.pctExito == null ? '—' : `${data.pctExito}%`}
              </p>
              <p className="text-[10px] text-gray-500">% de éxito</p>
            </div>
          </div>
          {data.detalle.length > 0 && (
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {data.detalle.slice(0, 15).map(d => (
                <div key={d.ordenId} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-1.5 ${d.convertida ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <span className="text-xs text-gray-700 truncate">{d.fecha} · {d.tipoEquipo || 'equipo'}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${d.convertida ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                    {d.convertida ? '✓ contrato' : 'sin convertir'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Separador de sección — todo el dashboard va en una sola pantalla con
// scroll (Hoy / Operativo / Comercial apilados), no en pestañas.
const SeccionDashboard = ({ titulo }) => (
  <div className="flex items-center gap-3 pt-2">
    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">{titulo}</h2>
    <div className="h-px bg-gray-200 flex-1" />
  </div>
);

const DashboardView = ({ onNavigate }) => {
  const { user } = useAuth();

  const [kanban, setKanban] = useState(null);
  const [bolsa, setBolsa] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [tareasDate, setTareasDate] = useState(null);
  const [cargaHoy, setCargaHoy] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('year');
  const [unreadEmails, setUnreadEmails] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [opsKpis, setOpsKpis] = useState(null);
  const [contratosEnRiesgo, setContratosEnRiesgo] = useState(null);
  const [contratosResumen, setContratosResumen] = useState(null);
  const [completadasHoy, setCompletadasHoy] = useState(null);
  const [rendimientoHoy, setRendimientoHoy] = useState(null);

  const fechaHoy = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  })();

  const cargar = useCallback(async (forceRefresh = false) => {
    const cacheKey = `dashboard_${fechaHoy}`;

    if (!forceRefresh) {
      const cached = getCached(cacheKey);
      if (cached) {
        const { kanban: k, bolsa: b, tareas: t, tareasDate: td, cargaHoy: c, opsKpis: ok, contratosEnRiesgo: cr, contratosResumen: cres, completadasHoy: ch, rendimientoHoy: rh } = cached.data;
        if (k) setKanban(k);
        if (b) setBolsa(b);
        if (t) setTareas(t);
        if (td) setTareasDate(td);
        if (c) setCargaHoy(c);
        if (ok) setOpsKpis(ok);
        if (cr !== undefined) setContratosEnRiesgo(cr);
        if (cres !== undefined) setContratosResumen(cres);
        if (ch) setCompletadasHoy(ch);
        if (rh !== undefined) setRendimientoHoy(rh);
        setLastUpdate(cached.ts);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      const fetchOpts = { headers: { ...authService.authHeader() }, ...(forceRefresh ? { cache: 'reload' } : {}) };
      // "Contratos en riesgo" (director de operaciones, pestaña Operativo) y
      // "Contratos" (admin, banner bajo Operaciones del día) reutilizan la
      // misma lectura de getMantenimientos + calcularKpisMantenimientos —
      // mismo criterio que arrEnRiesgo en PanelDireccion.
      const necesitaContratosEnRiesgo = user?.role === 'coordinador_operaciones';
      const necesitaContratosResumen  = user?.role === 'admin';
      const [kanbanRes, bolsaRes, tareasRes, workdayRes, opsKpisRes, mantsRes] = await Promise.all([
        fetch(`/.netlify/functions/getKanbanWorkOrders`, fetchOpts).then(r => r.json()),
        fetch(`/.netlify/functions/getBolsaTrabajo`, fetchOpts).then(r => r.json()),
        tasksService.getTasksForUser(user?.role || 'admin'),
        fetch(`/.netlify/functions/getWorkordersDay?fecha=${fechaHoy}`, fetchOpts).then(r => r.json()),
        fetch(`/.netlify/functions/getOperacionesKPIs`, fetchOpts).then(r => r.json()).catch(() => null),
        (necesitaContratosEnRiesgo || necesitaContratosResumen)
          ? fetch(`/.netlify/functions/getMantenimientos`, fetchOpts).then(r => r.json()).catch(() => null)
          : Promise.resolve(null),
      ]);

      const kanbanData  = kanbanRes.columnas  || null;
      const bolsaData   = bolsaRes.bolsa      || [];
      const tareasData  = tareasRes.success ? tareasRes.tasks : [];
      const tareasDateData = tareasRes.date || null;
      const cargaData   = workdayRes.debug?.distribucionTecnicos || {};
      const completadasHoyData = workdayRes.completadas || null;
      const rendimientoHoyData = typeof workdayRes.rendimientoHoy === 'number' ? workdayRes.rendimientoHoy : null;
      const opsKpisData = opsKpisRes?.success ? opsKpisRes.kpis : null;
      const mantsKpis = (necesitaContratosEnRiesgo || necesitaContratosResumen) && mantsRes?.success
        ? calcularKpisMantenimientos(mapEstados(mantsRes.mantenimientos || []))
        : null;
      const contratosEnRiesgoData = necesitaContratosEnRiesgo && mantsKpis ? mantsKpis.enRiesgo : null;
      const contratosResumenData = necesitaContratosResumen && mantsKpis ? {
        firmadosHoy: mantsKpis.firmadosHoy, pendienteFirmar: mantsKpis.pendienteFirmar,
        firmadosEsteMes: mantsKpis.nuevosEste, arr: mantsKpis.arr, mrr: mantsKpis.mrr,
      } : null;

      if (kanbanData) setKanban(kanbanData);
      setBolsa(bolsaData);
      setTareas(tareasData);
      setTareasDate(tareasDateData);
      setCargaHoy(cargaData);
      if (opsKpisData) setOpsKpis(opsKpisData);
      setContratosEnRiesgo(contratosEnRiesgoData);
      setContratosResumen(contratosResumenData);
      setCompletadasHoy(completadasHoyData);
      setRendimientoHoy(rendimientoHoyData);

      const ts = Date.now();
      setCached(cacheKey, { kanban: kanbanData, bolsa: bolsaData, tareas: tareasData, tareasDate: tareasDateData, cargaHoy: cargaData, opsKpis: opsKpisData, contratosEnRiesgo: contratosEnRiesgoData, contratosResumen: contratosResumenData, completadasHoy: completadasHoyData, rendimientoHoy: rendimientoHoyData });
      setLastUpdate(ts);
    } catch (e) {
      console.error('Dashboard error:', e);
    } finally {
      setLoading(false);
    }
  }, [user, fechaHoy]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    fetch('/.netlify/functions/getUnreadCount', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => { if (d.success) setUnreadEmails(d.unseen); })
      .catch(() => {});
  }, []);

  // Calcular métricas
  const todasLasOrdenes = kanban ? Object.entries(kanban).flatMap(([estado, cards]) =>
    cards.map(c => ({ ...c, estado }))
  ) : [];

  const ordenesFiltradas = useMemo(
    () => todasLasOrdenes.filter(c => dentroDelPeriodo(c.date, filtro)),
    [todasLasOrdenes.length, filtro] // eslint-disable-line
  );

  const formatTareasDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const dayName = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][d.getDay()];
    const monthName = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][month - 1];
    return `${dayName} ${day} ${monthName}`;
  };

  const enAlerta = ordenesFiltradas.filter(c => { const d = calcDias(c.date); return requiereAtencion(d); });
  const enAlertaOrdenadas = [...enAlerta].sort((a, b) => (calcDias(b.date) || 0) - (calcDias(a.date) || 0));

  const ESTADO_LABEL = {
    pendientes: 'Pendiente',
    pendientesMaterial: 'Pend. Material',
    pendientesPresupuesto: 'Pend. Presupuesto',
    pendientesSustitucion: 'Pend. Sustitución',
    ausente: 'Ausente',
  };

  const tareasAlta = tareas.filter(t => t.priority === 'high');

  const cargaTecnicos = useMemo(() => {
    const counts = {};
    Object.keys(TECNICOS_IDS).forEach(n => { counts[n] = cargaHoy[n] || 0; });
    return counts;
  }, [cargaHoy]);

  const skeleton = (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => (
          <div key={i} className="bg-white rounded-2xl border shadow-sm p-5 animate-pulse">
            <div className="w-10 h-10 bg-gray-100 rounded-xl mb-3" />
            <div className="h-6 bg-gray-100 rounded w-1/3 mb-2" />
            <div className="h-3 bg-gray-100 rounded w-2/3" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg border p-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            Buenos días, {user?.name?.split(' ')[0]} 👋
          </h2>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <p className="text-gray-400 text-sm">
              {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            {unreadEmails > 0 && (
              <button onClick={() => onNavigate('crm-email')}
                className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 font-medium transition-colors">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                {unreadEmails} email{unreadEmails !== 1 ? 's' : ''} sin leer
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Filtro de período */}
          <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-1">
            {FILTROS.map(f => (
              <button
                key={f.id}
                onClick={() => setFiltro(f.id)}
                className={[
                  'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                  filtro === f.id
                    ? 'bg-white text-red-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700',
                ].join(' ')}
              >
                {f.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => cargar(true)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm font-medium"
          >
            <Icons.Refresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {lastUpdate ? `Actualizar · ${formatAge(lastUpdate)}` : 'Actualizar'}
          </button>
        </div>
      </div>

      {/* Fichaje del día — para todo el personal de oficina excepto admin (no
          ficha). El historial propio (art. 34.9 ET) se consulta en el
          apartado Fichajes, no aquí. */}
      {user?.role !== 'admin' && <FichajeCardOficina user={user} />}

      {loading ? skeleton : (
        <>
          {/* ── HOY ── */}
          <SeccionDashboard titulo="Hoy" />

          {/* Avisos push (nueva venta admin, rutas sin cerrar admin+coordinador) */}
          {['admin', 'coordinador_operaciones'].includes(user?.role) && <VentaAdminNotificationCard role={user.role} />}

          {/* Errores de sincronización con StelOrder (6 intentos agotados) —
              mismo GESTION que el resto de tarjetas operativas de esta sección */}
          {['admin', 'coordinador_operaciones'].includes(user?.role) && <StelSyncErroresCard />}

          {/* Fallo de conexión del buzón de gastos de proveedor (ver
              procesarBandejaGastos.js / Facturas → Gastos) */}
          {['admin', 'coordinador_operaciones'].includes(user?.role) && <BuzonGastosErrorCard />}

          {/* Panel diario de operaciones — técnicos activos, órdenes de
              hoy/mañana, SLA y % de ruta cerrada (de getOperacionesKPIs) */}
          <PanelDiario
            cargaHoy={cargaTecnicos} onNavigate={onNavigate}
            rutasHoy={opsKpis?.rutas} slaAlerta={opsKpis?.alerta}
            completadasHoy={completadasHoy} rendimientoHoy={rendimientoHoy}
          />

          {/* Resumen de contratos de mantenimiento — solo admin */}
          {user?.role === 'admin' && contratosResumen && (
            <PanelContratos resumen={contratosResumen} onNavigate={onNavigate} />
          )}

          {/* Tareas del día */}
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            <div className="bg-blue-50 border-b border-blue-100 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                <h3 className="font-bold text-sm text-blue-700">
                  Tareas pendientes{tareasDate && <span className="font-normal"> · {formatTareasDate(tareasDate)}</span>}
                </h3>
              </div>
              <div className="flex items-center gap-1.5">
                {tareasAlta.length > 0 && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                    {tareasAlta.length} alta prioridad
                  </span>
                )}
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  {tareas.length}
                </span>
              </div>
            </div>
            <div className="px-5 py-3 max-h-96 overflow-y-auto">
              {tareas.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">🎉 Sin tareas pendientes</p>
              ) : (
                tareas.slice(0, 15).map(t => (
                  <div key={t.id} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
                    <span className="text-base flex-shrink-0 mt-0.5">
                      {t.priority === 'high' ? '🔴' : t.priority === 'medium' ? '🟡' : '🟢'}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {t.workorderData?.fullReference || t.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{t.description}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── OPERATIVO ── */}
          <SeccionDashboard titulo="Operativo" />

          {/* KPIs operativos — mismo backend que la Analítica de "Órdenes
              de trabajo" (getOperacionesKPIs.js), subconjunto curado */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<Icons.Layout className="w-5 h-5 text-red-600" />}
              color="bg-red-50"
              label="Backlog"
              value={opsKpis?.backlog?.valor ?? '—'}
              sub="Órdenes pendientes"
              onClick={() => onNavigate('v2-ordenes')}
            />
            <StatCard
              icon={<Icons.Clock className="w-5 h-5 text-green-600" />}
              color="bg-green-50"
              label="Bolsa de trabajo"
              value={bolsa.length}
              sub="Gestionadas sin programar"
              onClick={() => onNavigate('v2-ordenes')}
            />
            <StatCard
              icon={<span className="text-lg">✅</span>}
              color="bg-blue-50"
              label="Tasa de resolución"
              value={opsKpis?.completionRate?.valor != null ? `${opsKpis.completionRate.valor}%` : '—'}
              sub="últimos 7 días"
              onClick={() => onNavigate('v2-ordenes')}
            />
            <StatCard
              icon={<span className="text-lg">⏱️</span>}
              color="bg-indigo-50"
              label="Resolución media"
              value={opsKpis?.resolucion?.media != null ? `${opsKpis.resolucion.media}d` : '—'}
              sub="días desde creación"
              onClick={() => onNavigate('v2-ordenes')}
            />
            <StatCard
              icon={<span className="text-lg">⚠️</span>}
              color="bg-amber-50"
              label="Backlog envejecido"
              value={opsKpis?.aging?.pct != null ? `${opsKpis.aging.pct}%` : '—'}
              sub={opsKpis?.aging?.umbralDias ? `>${opsKpis.aging.umbralDias} días` : ''}
              onClick={() => onNavigate('v2-ordenes')}
            />
            {/* Solo director de operaciones — mismo criterio que "ARR en
                riesgo" de PanelDireccion, sin el importe en €. */}
            {user?.role === 'coordinador_operaciones' && contratosEnRiesgo !== null && (
              <StatCard
                icon={<span className="text-lg">📋</span>}
                color="bg-orange-50"
                label="Contratos en riesgo"
                value={contratosEnRiesgo}
                sub="renovar / vencido / devuelto"
                onClick={() => onNavigate('mantenimientos')}
              />
            )}
          </div>

          {/* Alertas */}
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-100 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <h3 className="font-bold text-sm text-amber-700">
                Órdenes en alerta — más de 7 días
                {filtro !== 'all' && (
                  <span className="ml-1.5 text-xs font-semibold bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-md">
                    {FILTROS.find(f => f.id === filtro)?.label}
                  </span>
                )}
              </h3>
              </div>
              <button
                onClick={() => onNavigate('v2-ordenes')}
                className="text-xs text-amber-600 hover:text-amber-800 font-medium"
              >
                Ver órdenes →
              </button>
            </div>
            <div className="px-5 py-3 max-h-96 overflow-y-auto">
              {enAlertaOrdenadas.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">🎉 Sin alertas activas</p>
              ) : (
                enAlertaOrdenadas.slice(0, 15).map(c => (
                  <AlertRow
                    key={c.id}
                    referencia={c.reference}
                    cliente={c.customerName}
                    dias={calcDias(c.date)}
                    estado={ESTADO_LABEL[c.estado] || c.estado}
                  />
                ))
              )}
            </div>
          </div>

          {/* Diagnóstico del motor de rutas — herramienta permanente de
              medición, mismo criterio de visibilidad que el resto de
              tarjetas operativas de gestión/dirección */}
          {['admin', 'coordinador_operaciones'].includes(user?.role) && <DiagnosticoRutasCard />}

          {/* Rendimiento de Joaquín (subcontrata, garantías Ariston) — mismo
              criterio de visibilidad que el diagnóstico del motor */}
          {['admin', 'coordinador_operaciones'].includes(user?.role) && <RendimientoJoaquinCard />}

          {/* Comparativa del piloto de notas IA, por técnico */}
          {['admin', 'coordinador_operaciones'].includes(user?.role) && <KpiPilotoCard />}

          {/* Oportunidades comerciales detectadas y su % de conversión, por técnico */}
          {['admin', 'coordinador_operaciones'].includes(user?.role) && <OportunidadesTecnicoCard />}

          {/* ── COMERCIAL (solo admin) ── */}
          {user?.role === 'admin' && (
            <>
              <SeccionDashboard titulo="Comercial" />
              <PanelDireccion onNavigate={onNavigate} />
            </>
          )}

          {/* Alertas ERP */}
          <AlertasERP />

          {/* Panel de notificaciones a técnicos — solo admin */}
          {user?.role === 'admin' && <NotificationsPanel />}

          {/* Accesos rápidos */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <h3 className="font-bold text-gray-700 text-sm mb-4">Accesos rápidos</h3>
            <div className="flex flex-wrap gap-3">
              {[
                { id: 'planner',   label: 'Planificador',     icon: <Icons.Calendar className="w-4 h-4" /> },
                { id: 'incidence', label: 'Nueva incidencia', icon: <Icons.Plus className="w-4 h-4" /> },
                { id: 'crm-email', label: 'Email',            icon: <Icons.Mail className="w-4 h-4" />, badge: unreadEmails },
                { id: 'v2-ordenes', label: 'Órdenes de trabajo', icon: <Icons.Clock className="w-4 h-4" /> },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className="relative flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-all text-sm font-medium text-gray-700"
                >
                  {item.icon}
                  {item.label}
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardView;
